import { DocUpdateResult } from '@common/collab/types/doc-update.type';
import { CollabSchema } from '@common/prosemirror/model/collab-schema';
import { CollabStep } from '@common/prosemirror/transform/collab-step.type';
import { StepJSON } from '@common/prosemirror/transform/json';
import { ProseMirrorNode, Schema } from 'prosemirror-model';
import { Step } from 'prosemirror-transform';

export function applyStepsToDoc(doc: ProseMirrorNode, steps: CollabStep[], schemaVersion: string, docVersion: number, clientId: string, userId: string): DocUpdateResult {
  const appliedAt = new Date().toISOString();
  const invertedSteps: CollabStep[] = [];

  // Apply the steps to the doc
  for (let i = 0; i < steps.length; i += 1) {
    let step = steps[i];

    step = toCollabStep(step, {
      appliedAt,
      clientID: clientId,
      docVersion: docVersion + i,
      externalCommit: step.externalCommit,
      restoredDocVersion: step.restoredDocVersion,
      restoredFrom: step.restoredFrom,
      schemaVersion,
      transactionId: step.transactionId,
      userID: userId
    });

    invertedSteps.push(toCollabStep(step.invert(doc), step));

    doc = step.apply(doc).doc;
  }

  // Return the results
  return {
    doc,
    invertedSteps,
    steps,
    version: docVersion + steps.length
  }
}

function toCollabStep(step: Step, stepMetaData: Omit<CollabStep, keyof Step>): CollabStep {
  const collabStep = step as CollabStep;

  // Copy over the step meta data
  collabStep.appliedAt = stepMetaData.appliedAt;
  collabStep.clientID = stepMetaData.clientID;
  collabStep.docVersion = stepMetaData.docVersion;
  collabStep.externalCommit = stepMetaData.externalCommit;
  collabStep.restoredDocVersion = stepMetaData.restoredDocVersion;
  collabStep.restoredFrom = stepMetaData.restoredFrom;
  collabStep.schemaVersion = stepMetaData.schemaVersion;
  collabStep.transactionId = stepMetaData.transactionId;
  collabStep.userID = stepMetaData.userID;

  return collabStep;
}

export function addTransactionIdToSteps(steps: Step[], transactionId: string) {
  steps.forEach((step: CollabStep) => {
    step.transactionId = transactionId;
  });
}

export function stepFromJSON(schema: Schema, json: StepJSON): CollabStep {
  const step = Step.fromJSON(schema, json) as CollabStep;
  step.appliedAt = json.appliedAt;
  step.clientID = json.clientID;
  step.docVersion = json.docVersion;
  step.externalCommit = json.externalCommit;
  step.restoredDocVersion = json.restoredDocVersion;
  step.restoredFrom = json.restoredFrom;
  step.schemaVersion = json.schemaVersion;
  step.transactionId = json.transactionId;
  step.userID = json.userID;
  return step;
}

export function stepsFromJSON(schema: Schema, steps: StepJSON[]): CollabStep[] {
  return Array.isArray(steps) ? steps.map(json => stepFromJSON(schema, json)) : [];
}

export function stepsAsJSON(steps: CollabStep[]): StepJSON[] {
  return steps.map<StepJSON>(step => {
    return {
      ...step.toJSON(),
      appliedAt: step.appliedAt,
      clientID: step.clientID,
      docVersion: step.docVersion,
      externalCommit: step.externalCommit,
      restoredDocVersion: step.restoredDocVersion,
      restoredFrom: step.restoredFrom,
      schemaVersion: step.schemaVersion,
      transactionId: step.transactionId,
      userID: step.userID
    };
  });
}

export function applyInvertedStepsToDoc(doc: ProseMirrorNode, docVersion: number, invertedSteps: StepJSON[], restoredDocVersion: number, restoredFrom: ISO8601DateString, transactionId: string, clientId: string, userId: string, schema: CollabSchema): DocUpdateResult {
  // For the types to be correct in the loop below use a new typed array variable
  const steps: (StepJSON | CollabStep)[] = invertedSteps;

  // Reverse the steps so they are applied in the correct order. Add a new transaction id and revisionFrom to the steps as well.
  // Use the two pointer technique to reverse the steps in place, convert them to CollabStep objects, and assign the transactionId in one pass.
  // This technique is used because there can be a lot of steps and this avoids creating more arrays and looping multiple times.
  for (let i = 0, j = steps.length - 1; i <= j; i += 1, j -= 1) {
    // Swap elements. In the case of an odd number of steps the middle step will be "swapped" with itself
    [steps[i], steps[j]] = [stepFromJSON(schema, steps[j] as StepJSON), stepFromJSON(schema, steps[i] as StepJSON)];

    // Assign transactionId to each step
    steps[i].transactionId = transactionId;
    steps[j].transactionId = transactionId;
    steps[i].restoredDocVersion = restoredDocVersion;
    steps[j].restoredDocVersion = restoredDocVersion;
    steps[i].restoredFrom = restoredFrom;
    steps[j].restoredFrom = restoredFrom;
  }

  // Apply the steps to the doc
  return applyStepsToDoc(doc, steps as CollabStep[], schema.version, docVersion, clientId, userId);
}
